import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users2, Award, Flame } from "lucide-react";
import { SITE_SHORT, siteCrest, subscribeSiteCrests } from "@/lib/sites";

const RANGES = [
  { key: "month", label: "This month" },
  { key: "week",  label: "Last 7 days" },
  { key: "all",   label: "All time" },
];

const ROLE_LABEL = { teacher: "Teacher", user: "User", secretary: "Secretary", principal: "Principal", it: "IT", maintenance: "Maintenance", custodian: "Custodian", mechanic: "Mechanic", admin: "Admin" };

const DEPT_COLORS = { IT: "#7C3AED", Maintenance: "#0D9488", Custodial: "#E11D48", Mechanic: "#D97706" };

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function Roster() {
  const { user } = useAuth();
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/reports/requester-roster", { params: { range } });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const [, setCrestTick] = useState(0);
  useEffect(() => subscribeSiteCrests(() => setCrestTick((n) => n + 1)), []);

  const rows = data?.rows || [];
  const max = Math.max(1, ...rows.map((r) => r.count));
  const isAdmin = user?.role === "admin";
  const scopeText = isAdmin
    ? "District-wide"
    : user?.sites?.length > 1
    ? `${user.sites.length} sites`
    : (SITE_SHORT[user?.sites?.[0]] || user?.site || "your site");

  return (
    <div className="p-4 md:p-8" data-testid="roster-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <Users2 className="h-3.5 w-3.5" /> Insights · ESUSD
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1">Requester Roster</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Who's submitting the most tickets · scope: {scopeText}
          </p>
        </div>
        <Tabs value={range} onValueChange={setRange}>
          <TabsList data-testid="roster-range-tabs">
            {RANGES.map((r) => (
              <TabsTrigger key={r.key} value={r.key} data-testid={`roster-range-${r.key}`}>{r.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="roster-metric-submitters">
          <div className="text-xs uppercase tracking-[0.1em] text-zinc-500">Submitters</div>
          <div className="mt-3 font-heading text-4xl font-light tracking-tighter text-zinc-950">{data?.total_submitters ?? 0}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="roster-metric-tickets">
          <div className="text-xs uppercase tracking-[0.1em] text-zinc-500">Tickets in range</div>
          <div className="mt-3 font-heading text-4xl font-light tracking-tighter text-zinc-950">{data?.total_tickets ?? 0}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="roster-metric-top">
          <div className="text-xs uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1">
            <Award className="h-3 w-3" /> Top submitter
          </div>
          <div className="mt-2 min-w-0">
            <div className="font-heading text-lg font-semibold text-zinc-950 truncate">{rows[0]?.requester_name || "—"}</div>
            <div className="text-xs text-zinc-500 truncate">
              {rows[0] ? `${rows[0].count} ticket${rows[0].count === 1 ? "" : "s"} · ${rows[0].requester_email}` : "No submissions yet"}
            </div>
          </div>
        </div>
      </div>

      {/* Roster table */}
      <div className="bg-white border border-zinc-200 rounded-md mt-6 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Rank</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Requester</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Site(s)</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Tickets</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">By dept</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Open</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Last</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-16">Loading roster…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-16">No submissions in this range.</TableCell></TableRow>
            )}
            {rows.map((r, i) => {
              const pct = Math.round((r.count / max) * 100);
              const isTop = i === 0 && r.count > 0;
              return (
                <TableRow key={r.requester_email + i} className="border-b border-zinc-100" data-testid={`roster-row-${i}`}>
                  <TableCell className="text-xs font-mono text-zinc-500 w-12">#{i + 1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-sm text-zinc-950 font-medium truncate">{r.requester_name || "—"}</div>
                      {isTop && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full">
                          <Flame className="h-2.5 w-2.5" /> Top
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{r.requester_email}</div>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-700">{ROLE_LABEL[r.role] || r.role || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(r.sites || []).slice(0, 4).map((s) => (
                        <div key={s} className="inline-flex items-center gap-1">
                          {siteCrest(s) && (
                            <img
                              src={siteCrest(s)}
                              alt=""
                              className="h-4 w-4 rounded-sm object-contain shrink-0"
                              data-testid={`roster-crest-${s}`}
                            />
                          )}
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-mono bg-zinc-100 text-zinc-700 border border-zinc-200">
                            {SITE_SHORT[s] || s}
                          </span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="w-48">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm tabular-nums w-6 text-right text-zinc-950 dark:text-white">{r.count}</span>
                      <div className="h-1.5 flex-1 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0055FF] rounded-full transition-[width] duration-500 ease-out"
                          style={{ width: `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {["IT", "Maintenance", "Custodial", "Mechanic"].map((d) => (
                        <span
                          key={d}
                          title={`${d}: ${r.by_department?.[d] || 0}`}
                          className="text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: (r.by_department?.[d] || 0) > 0 ? DEPT_COLORS[d] + "22" : "#f4f4f5",
                            color: (r.by_department?.[d] || 0) > 0 ? DEPT_COLORS[d] : "#a1a1aa",
                          }}
                        >
                          {d[0]}{r.by_department?.[d] || 0}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.open_count > 0 ? (
                      <Badge kind="status" value="Open" />
                    ) : <span className="text-xs text-zinc-400">0</span>}
                    {r.open_count > 0 && <span className="ml-1 text-xs text-zinc-500 tabular-nums">×{r.open_count}</span>}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">{timeAgo(r.last_submitted)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
